import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * Send a play/pause command to the iframe-based media provider that owns `iframe`.
 *
 * Dispatches the right postMessage payload for YouTube vs Vimeo. Only acts on
 * iframes that carry a `data-video-type` of `youtube` or `vimeo`; otherwise the
 * iframe is ignored (callers should still fall through to the `<video>` path).
 *
 * @param {{ dataset: { videoType?: string }, contentWindow?: Window | null } | null | undefined} iframe
 * @param {'play' | 'pause'} command
 * @returns {void}
 */ function postMediaCommand(iframe, command) {
  if (!iframe) return;
  var videoType = iframe.dataset && iframe.dataset.videoType;
  var isYouTube = videoType === 'youtube';
  var target = isYouTube
    ? 'https://www.youtube.com'
    : 'https://player.vimeo.com';
  if (!iframe.contentWindow) return;
  var payload = isYouTube
    ? '{"event":"command","func":"' +
      (command === 'play' ? 'playVideo' : 'pauseVideo') +
      '","args":""}'
    : '{"method":"' + command + '"}';
  iframe.contentWindow.postMessage(payload, target);
}
class DeferredMedia extends Component {
  isPlaying = false;
  #abortController = new AbortController();
  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      this.pauseMedia.bind(this),
      { signal: signal },
    );
    window.addEventListener(
      DialogCloseEvent.eventName,
      this.pauseMedia.bind(this),
      { signal: signal },
    );
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }
  /**
   * Toggle the play/pause icon visibility on the deferred-media control.
   *
   * @param {boolean} isPlaying Current playback state.
   * @returns {void}
   */ updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }
  /**
   * Reveal and start the deferred media content.
   *
   * @returns {void}
   */ showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };
  /**
   * Clone the deferred-media `<template>` into the DOM and auto-play when applicable.
   *
   * @param {boolean} [focus=true] Whether to focus the cloned content once inserted.
   * @returns {void}
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    const content =
      this.querySelector('template')?.content.firstElementChild?.cloneNode(
        true,
      );
    if (!content) return;
    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    if (focus && content instanceof HTMLElement) {
      content.focus();
    }
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    if (
      content instanceof HTMLVideoElement &&
      content.getAttribute('autoplay')
    ) {
      content.play();
    }
  }
  /**
   * Toggle between play and pause based on `this.isPlaying`.
   *
   * @returns {void}
   */ toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }
  /**
   * Resume playback of the deferred media (iframe or `<video>`).
   *
   * @returns {void}
   */ playMedia() {
    postMediaCommand(this.querySelector('iframe[data-video-type]'), 'play');
    this.querySelector('video')?.play();
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }
  /**
   * Pause playback of the deferred media (iframe or `<video>`).
   *
   * @returns {void}
   */ pauseMedia() {
    postMediaCommand(this.querySelector('iframe[data-video-type]'), 'pause');
    this.querySelector('video')?.pause();
    this.isPlaying = false;
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}
if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();
  /**
   * Load the template content (delegated to super) and bootstrap the model-viewer UI.
   *
   * @returns {void}
   */
  loadContent() {
    super.loadContent();
    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }
  /**
   * Pause the model-viewer UI in addition to the base deferred media.
   *
   * @returns {void}
   */ pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }
  /**
   * Resume the model-viewer UI in addition to the base deferred media.
   *
   * @returns {void}
   */ playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }
  /**
   * Initialize the model-viewer UI, wire pointer/click gestures to toggle playback,
   * and start playback once the viewer is ready.
   *
   * @param {true | undefined} errors Truthy when Shopify feature loading reported an error.
   * @returns {Promise<void>}
   */ async setupModelViewerUI(errors) {
    if (errors) return;
    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }
    if (!Shopify.ModelViewerUI) return;
    const element = this.querySelector('model-viewer');
    if (!element) return;
    const signal = this.#abortController.signal;
    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;
    this.playMedia();
    let pointerStartX = 0;
    let pointerStartY = 0;
    element.addEventListener(
      'pointerdown',
      (event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal: signal },
    );
    element.addEventListener(
      'click',
      (event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(
          distanceX * distanceX + distanceY * distanceY,
        );
        if (totalDistance < 10) {
          this.pauseMedia();
        }
      },
      { signal: signal },
    );
  }
  /**
   * Poll `Shopify.ModelViewerUI` for up to ~500ms before giving up.
   *
   * @returns {Promise<void>}
   */ async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;
    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}
if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
